import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock, Users, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { workflowSteps } from '../data/mock';

const HowWeWork = () => {
  const principles = [
    {
      icon: Users,
      title: "Humans Lead, AI Amplifies",
      description: "Our consultants leverage AI tools to deliver faster without compromising quality or human judgment."
    },
    {
      icon: Zap,
      title: "Agile & Iterative",
      description: "Continuous delivery with regular feedback loops ensures we stay aligned with your evolving needs."
    },
    {
      icon: CheckCircle2,
      title: "Single Accountability",
      description: "One team, one platform, one point of contact. We own the entire delivery lifecycle."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/40 via-white to-gray-50/60" />
        
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Our Proven
            <span className="bg-gradient-to-r from-gray-900 to-orange-400 bg-clip-text text-transparent"> Delivery Methodology</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            A structured approach that combines human expertise, AI acceleration, and proven frameworks to deliver exceptional results.
          </p>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Core Delivery Principles</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {principles.map((principle, index) => (
              <Card key={index} className="p-8 bg-white hover:shadow-2xl transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <principle.icon className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{principle.title}</h3>
                <p className="text-gray-600 leading-relaxed">{principle.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Steps */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our 5-Phase Delivery Model</h2>
            <p className="text-xl text-gray-600">
              From discovery to ongoing support, we guide you every step of the way
            </p>
          </div>

          <div className="space-y-8">
            {workflowSteps.map((step, index) => (
              <Card 
                key={index}
                className="p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-orange-400 bg-gradient-to-r from-white to-gray-50"
              >
                <div className="flex flex-col lg:flex-row gap-8 items-start">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                      {step.step}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-lg text-gray-600 mb-4 leading-relaxed">{step.description}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>Duration: {step.duration}</span>
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    <CheckCircle2 className="w-8 h-8 text-orange-500" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Flexible Engagement Models</h2>
            <p className="text-xl text-gray-600">
              Choose the model that best fits your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Fixed Price",
                description: "Well-defined scope with predictable costs and timelines",
                bestFor: "Projects with clear requirements",
                features: ["Fixed scope", "Guaranteed delivery", "Predictable budget"]
              },
              {
                title: "Time & Materials",
                description: "Flexible engagement for evolving requirements",
                bestFor: "Complex or exploratory projects",
                features: ["Flexible scope", "Agile approach", "Pay as you go"]
              },
              {
                title: "Managed Services",
                description: "Ongoing support and continuous improvement",
                bestFor: "Long-term partnerships",
                features: ["Dedicated team", "Proactive support", "SLA guarantees"]
              }
            ].map((model, index) => (
              <Card key={index} className="p-8 bg-white hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{model.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{model.description}</p>
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <p className="text-sm font-medium text-orange-600">Best for: {model.bestFor}</p>
                </div>
                <ul className="space-y-3">
                  {model.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Our Approach Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Our Approach Delivers Results
              </h2>
              <div className="space-y-6">
                {[
                  {
                    stat: "40-60%",
                    label: "Faster delivery than traditional consulting"
                  },
                  {
                    stat: "99.9%",
                    label: "Project success rate across 500+ engagements"
                  },
                  {
                    stat: "3x",
                    label: "ROI achieved within first year"
                  },
                  {
                    stat: "95%",
                    label: "Client satisfaction score"
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-6 p-6 bg-gradient-to-r from-orange-50 to-gray-50 rounded-xl">
                    <div className="text-4xl font-bold text-orange-600">{item.stat}</div>
                    <div className="text-gray-700 font-medium">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <Card className="p-10 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
              <h3 className="text-2xl font-bold mb-6">The AiFlo Advantage</h3>
              <ul className="space-y-4">
                {[
                  "AI-augmented consultants delivering 10x productivity",
                  "Single accountability eliminating vendor fragmentation",
                  "Platform-agnostic recommendations focused on your success",
                  "Proven frameworks from 500+ successful projects",
                  "Continuous optimization and managed services"
                ].map((advantage, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-orange-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-200 leading-relaxed">{advantage}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-orange-500" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Experience the AiFlo Difference
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Let's discuss how our proven methodology can accelerate your success.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-xl shadow-2xl">
              Schedule a Discovery Call
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowWeWork;
